import {PdfGeneratorService} from "@/modules/PdfGeneratorService";
import {PdfGeneratorManager} from "@/modules/PdfGeneratorManager";

export const pdfMixin = {
    data: {
        companyLogo: null,
    },
    methods: {
        // Fetch company's logo if companyUrl is found and user.company.id is defined, embedd the logo to the pdf
        // if not, just resolve gracefully and let this.companyLogo be null
        fetchCompanyLogo() {
            return new Promise((resolve) => {
                if (!this.companyUrl) {
                    resolve()
                    return
                }

                if(this.user && this.user.company && this.user.company.id) {
                    this.restFetch(this.companyUrl + '/' + this.user.company.id + '/photo', (response) => {
                        if(response && response.data) {
                            this.companyLogo = response.data
                        }
                        resolve()
                    })
                } else {
                    resolve()
                }
            })
        },

        // Mostly used part of the mixin. Initialize and prepare the PDF document for further use.
        // include logos, fonts, colors etc. basic stuff for pdf files.
        async initializePdf(addFooterLogo = true, landscape = false) {
            const pdfGeneratorManager = new PdfGeneratorManager()

            await this.fetchCompanyLogo()

            PdfGeneratorService.companyLogoBytes = this.companyLogo

            const pdfDoc = await pdfGeneratorManager.createStandardRoutaReportPdf(addFooterLogo, landscape)
            await PdfGeneratorService.cacheRoutaFooterLogo()

            const footerLogo = pdfGeneratorManager.embeddedImages.footer
            const companyFooterLogo = pdfGeneratorManager.embeddedImages.companyLogo || null
            const pdfService = pdfGeneratorManager.pdfGeneratorService

            const fonts = {
                fontBold: pdfGeneratorManager.getBoldFont(),
                fontText: pdfGeneratorManager.getTextFont(),
                fontHeader: pdfGeneratorManager.getHeaderFont()
            }

            const colors = {
                boldColor: pdfService.getBoldColor(),
                textColor: pdfService.getTextColor(),
                headerColor: pdfService.getHeaderColor()
            }

            this.pdfConfig = {
                pdfDoc,
                pdfGeneratorManager,
                pdfService,
                footerLogo,
                companyFooterLogo,
                fonts,
                colors
            }

            return this.pdfConfig
        },

        async addPdfHeaderWithMap(title, screenshot, subtitle, dateLabel, periodText = undefined){
            const {
                pdfDoc,
                pdfService,
                footerLogo,
                fonts,
                colors,
                companyFooterLogo
            } = this.pdfConfig;

            let nextYPosition = undefined

            const dateText = dateLabel ? dateLabel + ': ' + this.getCurrentDay() : this.getCurrentDay()

            nextYPosition = pdfService.addText(pdfDoc, title, fonts.fontHeader, 20, nextYPosition, undefined, undefined, undefined, footerLogo, companyFooterLogo, colors.headerColor)
            nextYPosition = pdfService.addText(pdfDoc, dateText, fonts.fontHeader, 14, nextYPosition, undefined, undefined, undefined, footerLogo, companyFooterLogo)

            if (subtitle) {
                nextYPosition = pdfService.addText(pdfDoc, subtitle, fonts.fontHeader, 14, nextYPosition, undefined, undefined, undefined, footerLogo, companyFooterLogo)
            }

            if (periodText !== undefined) {
                nextYPosition = pdfService.addText(pdfDoc, this.$t('work_assignment.workassignment_period_label') + ' ' + periodText, fonts.fontHeader, 14, nextYPosition, undefined, undefined, undefined, footerLogo, companyFooterLogo)
            }

            const mapPhoto = await pdfDoc.embedJpg(screenshot)
            nextYPosition = pdfService.addImage(pdfDoc, undefined, mapPhoto, undefined, nextYPosition, footerLogo, undefined, 1, 1, 1, companyFooterLogo)
            nextYPosition -= 15

            return nextYPosition
        },

        // this part is basically only used where observations are needed in the pdf
        // ObservationDetailsReport and TripWithObservationsReport components use this part of the mixin.
        async addObservationToPdf(observation, photos, nextYPosition, observationIndex) {
            const {
                pdfDoc,
                pdfService,
                fonts,
                colors,
                footerLogo,
                companyFooterLogo
            } = this.pdfConfig;

            const { fontHeader, fontText, fontBold } = fonts
            const { headerColor, textColor, boldColor } = colors

            let yPosition = nextYPosition < 0 ? pdfService.defaultVerticalMargin : nextYPosition
            const lines = Math.round((observation.info ? observation.info.length : 1) / 77)
            //224 is observation length estimate
            const addNewPage = yPosition - (100 + lines) < pdfService.footerZone

            if (observation.photos.length >= 1 || addNewPage) {
                nextYPosition -= 800
            }

            nextYPosition -= 10

            nextYPosition = pdfService.addText(pdfDoc, observation.type.name ? observation.type.name : ' ', fontHeader, 16, nextYPosition, undefined, undefined, undefined, footerLogo, companyFooterLogo, headerColor)
            if (observation.info) {
                nextYPosition = pdfService.addText(pdfDoc, observation.info, fontText, 10, nextYPosition, undefined, undefined, undefined, footerLogo, companyFooterLogo, textColor)
            }

            const images = []

            for (let photo of photos) {
                let image
                if (this.isPng(photo)) {
                    image = await pdfDoc.embedPng(photo)
                } else {
                    image = await pdfDoc.embedJpg(photo)
                }
                images.push(image)
            }

            //Observation images
            if (images.length > 1 && this.showAllPhotos) {
                nextYPosition = pdfService.addImages(pdfDoc, undefined, images, undefined, nextYPosition, footerLogo, 1, 1, 2, 3, 1, companyFooterLogo)
                nextYPosition -= 20
            } else if (images.length >= 1 || !this.showAllPhotos && images.length >= 1) {
                nextYPosition = pdfService.addImage(pdfDoc, undefined, images[0], undefined, nextYPosition, footerLogo, 200, 1, 1, undefined, companyFooterLogo, 0, true)
                nextYPosition -= 20
            }

            // Check if there's enough space for map + table (approximately 300px needed)
            if (nextYPosition < pdfService.footerZone + 250) {
                pdfDoc.addPage()
                const newPage = pdfDoc.getPage(pdfDoc.getPages().length - 1)
                nextYPosition = newPage.getHeight() - pdfService.defaultVerticalMargin
            }

            const observationMapImage = await this.takePhotoOfMap(observationIndex)
            const mapObservationJpg = await pdfDoc.embedJpg(observationMapImage)

            const currentPages = pdfDoc.getPages().length

            // Map full width below the image
            const mapHeight = 220
            const imageY = pdfService.addImage(pdfDoc, undefined, mapObservationJpg, undefined, nextYPosition, footerLogo, undefined, 1, 1, 1, companyFooterLogo, mapHeight, false)

            const newPages = pdfDoc.getPages().length

            //sync components Y position
            if (newPages !== currentPages) {
                const newPage = pdfDoc.getPage(pdfDoc.getPages().length - 1)
                nextYPosition = newPage.getHeight() - this.defaultVerticalMargin
            } else {
                nextYPosition = imageY
            }

            nextYPosition -= 10

            // Transform observation details into table format: headers row + data row
            const details = this.observationDetails(observation)
            const headers = details.map(d => d[0])
            const values = details.map(d => d[1])
            const tableY = pdfService.addTable([headers, values], pdfDoc, {
                textFont: fontText,
                headerFont: fontBold,
                textFontSize: 8,
                headerFontSize: 8,
                textColor: textColor,
                headerFontColor: boldColor
            }, nextYPosition, undefined, undefined, footerLogo, 1, 1, false, companyFooterLogo)

            nextYPosition = tableY - 5

            if (observation.photos.length === 0) {
                nextYPosition -= 20
            }
            return nextYPosition;
        },

        // this part is basically only used where observations are needed in the pdf
        // ObservationDetailsReport and TripWithObservationsReport components use this part of the mixin.
        async addSignToPdf(sign, name, photos, nextYPosition, index) {
            const {
                pdfDoc,
                pdfService,
                fonts,
                colors,
                footerLogo,
                companyFooterLogo
            } = this.pdfConfig;

            const { fontHeader, fontText, fontBold } = fonts
            const { headerColor, textColor, boldColor } = colors

            let yPosition = nextYPosition < 0 ? pdfService.defaultVerticalMargin : nextYPosition
            const lines = Math.round((sign.info ? sign.info.length : 1) / 77)
            // 150 is observation length estimate
            const addNewPage = yPosition - (250 + lines) < pdfService.footerZone

            if (sign.photos.length >= 1 || addNewPage) {
                nextYPosition -= 800
            }

            nextYPosition -= 10


            const svgUrl = this.getImageSource(sign.sign)
            const pngBytes = await this.svgUrlToPngBytes(
                svgUrl,
                100,
                100
            )
            const pdfImage = await pdfDoc.embedPng(pngBytes)

            nextYPosition = pdfService.addTextWithIcon(
                pdfDoc,
                name,
                pdfImage,
                fontHeader,
                16,
                nextYPosition,
                undefined,
                undefined,
                undefined,
                footerLogo,
                companyFooterLogo,
                headerColor
            )

            if (sign.info) {
                nextYPosition = pdfService.addText(pdfDoc, sign.info, fontText, 10, nextYPosition, undefined, undefined, undefined, footerLogo, companyFooterLogo, textColor)
            }

            const images = []

            for (let photo of photos) {
                let image
                if (this.isPng(photo)) {
                    image = await pdfDoc.embedPng(photo)
                } else {
                    image = await pdfDoc.embedJpg(photo)
                }
                images.push(image)
            }

            // Images
            if (images.length > 1 && this.showAllPhotos) {
                nextYPosition = pdfService.addImages(pdfDoc, undefined, images, undefined, nextYPosition, footerLogo, 1, 1, 2, 3, 1, companyFooterLogo)
                nextYPosition -= 20
            } else if (images.length >= 1 || !this.showAllPhotos && images.length >= 1) {
                nextYPosition = pdfService.addImage(pdfDoc, undefined, images[0], undefined, nextYPosition, footerLogo, 200, 1, 1, undefined, companyFooterLogo, 0, true)
                nextYPosition -= 20
            }

            // Check if there's enough space for map + table (approximately 300px needed)
            if (nextYPosition < pdfService.footerZone + 250) {
                pdfDoc.addPage()
                const newPage = pdfDoc.getPage(pdfDoc.getPages().length - 1)
                nextYPosition = newPage.getHeight() - pdfService.defaultVerticalMargin
            }

            const mapImage = await this.takePhotoOfMap(index)
            const mapJpg = await pdfDoc.embedJpg(mapImage)

            const currentPages = pdfDoc.getPages().length

            // Map full width below the image
            const mapHeight = 220
            const imageY = pdfService.addImage(pdfDoc, undefined, mapJpg, undefined, nextYPosition, footerLogo, undefined, 1, 1, 1, companyFooterLogo, mapHeight, false)

            const newPages = pdfDoc.getPages().length

            //sync components Y position
            if (newPages !== currentPages) {
                const newPage = pdfDoc.getPage(pdfDoc.getPages().length - 1)
                nextYPosition = newPage.getHeight() - this.defaultVerticalMargin
            } else {
                nextYPosition = imageY
            }

            nextYPosition -= 10

            // Transform observation details into table format: headers row + data row
            const details = this.signDetails(sign, name)
            const headers = details.map(d => d[0])
            const values = details.map(d => d[1])
            const tableY = pdfService.addTable([headers, values], pdfDoc, {
                textFont: fontText,
                headerFont: fontBold,
                textFontSize: 8,
                headerFontSize: 8,
                textColor: textColor,
                headerFontColor: boldColor
            }, nextYPosition, undefined, undefined, footerLogo, 1, 1, false, companyFooterLogo)

            nextYPosition = tableY - 5

            if (sign.photos.length === 0) {
                nextYPosition -= 20
            }
            return nextYPosition;
        },


        // this part is basically only used where work assignments are needed in the pdf
        // WorkAssignmentDetailsReport.vue uses this
        async addAssignmentToPdf(assignment, photos, nextYPosition, index) {
            const {
                pdfDoc,
                pdfService,
                fonts,
                colors,
                footerLogo,
                companyFooterLogo
            } = this.pdfConfig;

            const { fontHeader, fontText, fontBold } = fonts
            const { headerColor, textColor, boldColor } = colors

            let yPosition = nextYPosition < 0 ? pdfService.defaultVerticalMargin : nextYPosition
            const addNewPage = yPosition - 350 < pdfService.footerZone

            if (photos.length >= 1 || addNewPage) {
                nextYPosition -= 800
            }

            nextYPosition -= 10

            nextYPosition = pdfService.addText(pdfDoc, this.getTitle(assignment), fontHeader, 16, nextYPosition, undefined, undefined, undefined, footerLogo, companyFooterLogo, headerColor)

            const images = []

            for (let photo of photos) {
                let image
                if (this.isPng(photo)) {
                    image = await pdfDoc.embedPng(photo)
                } else {
                    image = await pdfDoc.embedJpg(photo)
                }
                images.push(image)
            }

            // Assignment images
            if (images.length > 1 && this.showAllPhotos) {
                nextYPosition = pdfService.addImages(pdfDoc, undefined, images, undefined, nextYPosition, footerLogo, 1, 1, 2, 3, 1, companyFooterLogo)
                nextYPosition -= 20
            } else if (images.length >= 1 || !this.showAllPhotos && images.length >= 1) {
                nextYPosition = pdfService.addImage(pdfDoc, undefined, images[0], undefined, nextYPosition, footerLogo, 200, 1, 1, undefined, companyFooterLogo, 0, true)
                nextYPosition -= 20
            }

            // Check if there's enough space for map + table (approximately 300px needed)
            if (nextYPosition < pdfService.footerZone + 300) {
                pdfDoc.addPage()
                const newPage = pdfDoc.getPage(pdfDoc.getPages().length - 1)
                nextYPosition = newPage.getHeight() - pdfService.defaultVerticalMargin
            }

            const mapImage = await this.takePhotoOfMap(assignment.id)
            if (mapImage) {
                // Not all assignments have location
                const mapJpg = await pdfDoc.embedJpg(mapImage)

                const currentPages = pdfDoc.getPages().length

                // Map full width below the image
                const mapHeight = 220
                const imageY = pdfService.addImage(pdfDoc, undefined, mapJpg, undefined, nextYPosition, footerLogo, undefined, 1, 1, 1, companyFooterLogo, mapHeight, false)

                const newPages = pdfDoc.getPages().length

                //sync components Y position
                if (newPages !== currentPages) {
                    const newPage = pdfDoc.getPage(pdfDoc.getPages().length - 1)
                    nextYPosition = newPage.getHeight() - this.defaultVerticalMargin
                } else {
                    nextYPosition = imageY
                }

                nextYPosition -= 20
            }

            // Transform assignment details into table format: headers row + data row
            const details = this.assignmentDetails(assignment)
            const headers = details.map(d => d[0])
            const values = details.map(d => d[1])
            const tableY = pdfService.addTable([headers, values], pdfDoc, {
                textFont: fontText,
                headerFont: fontBold,
                textFontSize: 8,
                headerFontSize: 8,
                textColor: textColor,
                headerFontColor: boldColor
            }, nextYPosition, undefined, undefined, footerLogo, 1, 1, false, companyFooterLogo)

            nextYPosition = tableY - 5

            if (photos.length === 0) {
                nextYPosition -= 20
            }
            return nextYPosition;
        },

        assignmentDetails(assignment) {
            return [
                [this.$t('work_assignment.id'), assignment.id],
                [this.$t('work_assignment.task'), this.getTitle(assignment)],
                [this.$t('orders.status'), this.getStatusString(assignment.status)],
                [this.$t('area_report_details.info'), this.getAssignmentInfo(assignment)],
            ]
        },

        signDetails(sign, name) {
            return [
                [this.$t('work_assignment.id'), sign.id],
                [this.$t('road_sign.sign'), name],
                [this.$t('road_sign.value'), sign.value],
                [this.$t('road_sign.created_at'), this.toLocalTime(sign.created_at)],
                [this.$t('road_sign.removed_at'), sign.removed_at? this.toLocalTime(sign.removed_at) : '' ],
                [this.$t('contracts.name'), sign.contract ? sign.contract.name : ''],
            ]
        },

        async svgUrlToPngBytes(svgUrl, width = 100, height = 100) {
            if (!svgUrl) {
                throw new Error('SVG URL is missing')
            }

            const response = await fetch(svgUrl)

            if (!response.ok) {
                throw new Error(
                    `Could not load SVG "${svgUrl}": HTTP ${response.status}`
                )
            }

            const svgText = await response.text()

            const svgBlob = new Blob([svgText], {
                type: 'image/svg+xml;charset=utf-8'
            })

            const objectUrl = URL.createObjectURL(svgBlob)

            try {
                const image = await new Promise((resolve, reject) => {
                    const img = new Image()

                    img.onload = () => resolve(img)

                    img.onerror = () => {
                        reject(
                            new Error(`Could not render SVG "${svgUrl}"`)
                        )
                    }

                    img.src = objectUrl
                })

                const canvas = document.createElement('canvas')
                canvas.width = width
                canvas.height = height

                const context = canvas.getContext('2d')

                if (!context) {
                    throw new Error('Could not create canvas context')
                }

                context.clearRect(0, 0, width, height)

                const sourceWidth = image.naturalWidth || image.width || width
                const sourceHeight = image.naturalHeight || image.height || height

                const scale = Math.min(
                    width / sourceWidth,
                    height / sourceHeight
                )

                const targetWidth = sourceWidth * scale
                const targetHeight = sourceHeight * scale

                context.drawImage(
                    image,
                    (width - targetWidth) / 2,
                    (height - targetHeight) / 2,
                    targetWidth,
                    targetHeight
                )

                const pngBlob = await new Promise((resolve, reject) => {
                    canvas.toBlob(blob => {
                        if (blob) {
                            resolve(blob)
                        } else {
                            reject(
                                new Error(`Could not convert SVG "${svgUrl}" to PNG`)
                            )
                        }
                    }, 'image/png')
                })

                return await pngBlob.arrayBuffer()
            } finally {
                URL.revokeObjectURL(objectUrl)
            }
        }

    }
}
